import React, { useEffect, useState } from "react";
import { MapPin, Mail, Phone, Clock, PawPrint, Facebook, Twitter, Instagram, Youtube } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";

const infoCards = [
  { icon: MapPin, title: "Our Clinic", lines: ["Swadlincote", "DE11"] },
  { icon: Mail, title: "Email Support", lines: ["info@moggiesanddoggiesvets.com"] },
  { icon: Phone, title: "Let's Talk", lines: ["Phone : 07535 341283"] },
  { icon: Clock, title: "Opening Hours", lines: ["Monday – Saturday", "9:00 AM – 6:00 PM"] },
];

const inputCls =
  "w-full rounded-xl border border-pawelle-ink/12 bg-white px-4 py-3.5 font-body text-[15px] text-pawelle-ink outline-none transition focus:border-pawelle-primary placeholder-pawelle-body/70";

const ContactUs = () => {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  useEffect(() => {
    document.title = "Contact Us – Moggies & Doggies Veterinary Care";
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-pawelle-body">
      <SiteHeader />
      <main>
        <PageBanner
          title="Contact Us"
          subtitle="Contact Moggies & Doggies Today for Personalized Pet Support"
          crumbs={["Contact Us"]}
        />

        {/* Info cards */}
        <section className="mx-auto max-w-[1290px] px-5 py-16 md:py-20">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {infoCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} animation="fadeInUp" delay={i * 90} className="group rounded-[28px] bg-pawelle-bg p-8 text-center transition-all duration-300 hover:bg-pawelle-primary">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-pawelle-primary transition-colors group-hover:bg-white/15 group-hover:text-white">
                    <Icon className="h-8 w-8" />
                  </div>
                  <h3 className="mt-5 font-display text-[20px] font-semibold text-pawelle-ink transition-colors group-hover:text-white">{c.title}</h3>
                  <div className="mt-3 space-y-1">
                    {c.lines.map((l) => (
                      <p key={l} className="font-body text-[15px] text-pawelle-body transition-colors group-hover:text-white/85">{l}</p>
                    ))}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* Form + map */}
        <section className="bg-pawelle-bg py-20 md:py-28">
          <div className="mx-auto max-w-[1290px] px-5">
            <div className="grid gap-12 lg:grid-cols-2">
              <div>
                <Reveal animation="rubberBand">
                  <span className="inline-flex items-center gap-2 font-display text-[15px] font-semibold text-pawelle-primary">
                    <PawPrint className="h-5 w-5" /> Get In Touch
                  </span>
                </Reveal>
                <Reveal animation="fadeInUp" delay={100}>
                  <p className="mt-3 font-display text-[19px] font-medium text-pawelle-ink/80">Let's Connect and Talk About Pet Wellness</p>
                  <h2 className="mt-2 max-w-lg font-display text-[30px] font-bold leading-tight text-pawelle-ink md:text-[38px]">
                    Contact Moggies & Doggies for Trusted Pet Care
                  </h2>
                </Reveal>
                <Reveal animation="fadeInUp" delay={150} className="mt-8">
                  <h4 className="font-display text-[18px] font-semibold text-pawelle-ink">Follow Our Social Media</h4>
                  <div className="mt-4 flex gap-3">
                    {[Facebook, Twitter, Instagram, Youtube].map((Icon, i) => (
                      <a key={i} href="#" className="flex h-11 w-11 items-center justify-center rounded-full bg-pawelle-primary text-white transition hover:bg-pawelle-primary-dark" aria-label="social">
                        <Icon className="h-5 w-5" />
                      </a>
                    ))}
                  </div>
                </Reveal>
              </div>

              <Reveal animation="fadeInRight" className="rounded-[28px] bg-white p-6 shadow-[0_24px_60px_rgba(67,48,109,0.08)] md:p-8">
                <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
                  <input className={inputCls} placeholder="Your Name" value={form.name} onChange={set("name")} required />
                  <input className={inputCls} placeholder="Company" value={form.company} onChange={set("company")} />
                  <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                  <input className={inputCls} type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
                  <input className={`${inputCls} sm:col-span-2`} placeholder="Subject" value={form.subject} onChange={set("subject")} />
                  <textarea className={`${inputCls} sm:col-span-2 min-h-[120px] resize-y`} placeholder="Message" value={form.message} onChange={set("message")} />
                  <button type="submit" className="sm:col-span-2 rounded-xl bg-pawelle-primary px-6 py-4 font-display text-[16px] font-medium text-white transition-all duration-300 hover:bg-pawelle-primary-dark">
                    {sent ? "Message Sent!" : "Send Message"}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="mx-auto max-w-[1290px] px-5 py-16">
          <Reveal animation="fadeInUp" className="overflow-hidden rounded-[28px] shadow-[0_24px_60px_rgba(67,48,109,0.08)]">
            <iframe
              title="Moggies & Doggies location"
              src="https://www.google.com/maps?q=Swadlincote%20DE11&output=embed"
              className="h-[420px] w-full border-0"
              loading="lazy"
            />
          </Reveal>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactUs;
